import { requireEnv } from '@/lib/env'
/**
 * Public Apollo Client for Guest Chatbot
 *
 * A lightweight Apollo client for the public chatbot widget that:
 * - Uses anonymous Nhost authentication
 * - Supports WebSocket subscriptions for real-time messages
 * - Is separate from the main authenticated Apollo client
 */

import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  split,
  from,
} from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient as createWsClient, type Client as WsClient } from 'graphql-ws'
import { createClient } from '@nhost/nhost-js'

// Refresh the anonymous token only when it's within this many seconds of expiry (SDK handles it).
const REFRESH_MARGIN_SEC = 60

// ============================================================================
// ENVIRONMENT CONFIGURATION
// ============================================================================

const GRAPHQL_HTTP_URL =
  requireEnv(process.env.NEXT_PUBLIC_NHOST_GRAPHQL_URL, 'NEXT_PUBLIC_NHOST_GRAPHQL_URL')

const GRAPHQL_WS_URL = GRAPHQL_HTTP_URL.replace('https://', 'wss://').replace('http://', 'ws://')

const AUTH_URL = requireEnv(process.env.NEXT_PUBLIC_NHOST_AUTH_URL, 'NEXT_PUBLIC_NHOST_AUTH_URL')

// Type for the Nhost client returned by createClient
type NhostClient = ReturnType<typeof createClient>

// ============================================================================
// PUBLIC NHOST CLIENT - For anonymous authentication
// ============================================================================

let publicNhostClient: NhostClient | null = null

export function getPublicNhostClient(): NhostClient {
  if (!publicNhostClient) {
    publicNhostClient = createClient({
      authUrl: AUTH_URL,
      graphqlUrl: GRAPHQL_HTTP_URL,
      storageUrl: requireEnv(process.env.NEXT_PUBLIC_NHOST_STORAGE_URL, 'NEXT_PUBLIC_NHOST_STORAGE_URL'),
      functionsUrl: requireEnv(process.env.NEXT_PUBLIC_NHOST_FUNCTIONS_URL, 'NEXT_PUBLIC_NHOST_FUNCTIONS_URL'),
    })
  }
  return publicNhostClient
}

/**
 * Sign in anonymously and return the session
 */
export async function signInAnonymously(metadata?: Record<string, unknown>): Promise<{
  success: boolean
  userId?: string
  accessToken?: string
  error?: string
}> {
  const client = getPublicNhostClient()

  try {
    // SDK v4.x: signInAnonymous returns FetchResponse<SessionPayload>
    const result = await client.auth.signInAnonymous({
      metadata,
    })

    // The session is in result.body.session (SDK v4.x)
    const session = result.body.session
    if (!session) {
      return { success: false, error: 'No session returned' }
    }

    return {
      success: true,
      userId: session.user?.id,
      accessToken: session.accessToken,
    }
  } catch (error) {
    console.error('[PublicApollo] Anonymous sign-in exception:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}

/**
 * Get the current anonymous session from storage
 * SDK v4.x uses getUserSession() on the main client, not auth.getSession()
 */
export function getAnonymousSession() {
  const client = getPublicNhostClient()
  return client.getUserSession()
}

/**
 * Check if we have a valid anonymous session
 */
export function hasValidAnonymousSession(): boolean {
  const session = getAnonymousSession()
  if (!session?.accessToken) return false
  // Session exists with access token - consider it valid
  // The SDK handles token refresh automatically
  return !!session.user?.id
}

/**
 * Sign out anonymous user
 */
export async function signOutAnonymous(): Promise<void> {
  const client = getPublicNhostClient()
  const session = client.getUserSession()
  if (session?.refreshToken) {
    await client.auth.signOut({ refreshToken: session.refreshToken })
  }
  client.clearSession()
}

// ============================================================================
// APOLLO CLIENT SINGLETON
// ============================================================================

// Apollo Client v4: ApolloClient is no longer generic
let publicApolloClient: ApolloClient | null = null
let wsClient: WsClient | null = null

/**
 * Create or get the public Apollo client
 */
export function getPublicApolloClient(): ApolloClient {
  if (publicApolloClient) {
    return publicApolloClient
  }

  // Auth link - refresh the anonymous token pre-emptively (SDK only hits the network near expiry),
  // then attach it. Mirrors the main client; no separate JWT-error refresh-and-replay needed.
  const authLink = new SetContextLink(async (prevContext) => {
    const headers = (prevContext.headers as Record<string, string>) ?? {}
    let session
    try {
      session = await getPublicNhostClient().refreshSession(REFRESH_MARGIN_SEC)
    } catch {
      session = getAnonymousSession()
    }
    const accessToken = session?.accessToken
    return {
      headers: {
        ...headers,
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        'Content-Type': 'application/json',
      },
    }
  })

  // HTTP link
  const httpLink = new HttpLink({
    uri: GRAPHQL_HTTP_URL,
  })

  // WebSocket link for subscriptions
  wsClient = createWsClient({
    url: GRAPHQL_WS_URL,
    lazy: true,
    connectionParams: async () => {
      // Delegate the near-expiry refresh to the SDK (same margin as HTTP).
      let session
      try {
        session = await getPublicNhostClient().refreshSession(REFRESH_MARGIN_SEC)
      } catch {
        session = getAnonymousSession()
      }
      return {
        headers: {
          Authorization: session?.accessToken ? `Bearer ${session.accessToken}` : '',
        },
      }
    },
    retryAttempts: 5,
    shouldRetry: (errOrCloseEvent: unknown) => {
      // Don't retry permanent errors (validation, auth)
      if (errOrCloseEvent && typeof errOrCloseEvent === 'object' && 'code' in errOrCloseEvent) {
        const code = (errOrCloseEvent as { code: number }).code
        if (code === 4400 || code === 4401 || code === 4409 || code === 4429) {
          return false
        }
      }
      return true
    },
    on: {
      connected: () => {
        console.warn('[PublicApollo] WebSocket connected')
      },
      closed: (event) => {
        console.warn('[PublicApollo] WebSocket closed', event)
      },
      error: (error) => {
        console.error('[PublicApollo] WebSocket error:', error)
      },
    },
  })

  const wsLink = new GraphQLWsLink(wsClient)

  // Split link - use WebSocket for subscriptions, HTTP for queries/mutations
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      )
    },
    wsLink,
    from([authLink, httpLink])
  )

  // Create the Apollo client
  publicApolloClient = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache({
      typePolicies: {
        chat_sessions: {
          keyFields: ['id'],
        },
        chat_messages: {
          keyFields: ['id'],
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
      },
      query: {
        fetchPolicy: 'network-only',
      },
    },
  })

  return publicApolloClient
}

/**
 * Reconnect WebSocket (useful after token refresh)
 */
export function reconnectPublicWebSocket(): void {
  if (wsClient) {
    // Terminate and reconnect
    wsClient.terminate()
  }
}

/**
 * Reset the public Apollo client (useful for cleanup)
 */
export function resetPublicApolloClient(): void {
  if (wsClient) {
    wsClient.terminate()
    wsClient = null
  }
  if (publicApolloClient) {
    publicApolloClient.clearStore()
    publicApolloClient = null
  }
  publicNhostClient = null
}
